import { useEffect, useState } from "react";
import { Card, Container, Tabs, Divider } from "@mantine/core";
import { getPatient } from "../../utils/api-communication/RestApiCli";

import PatientInfoCard from "../new_test_ui/new_test_steps/common/PatientInfoCard";
import { useTranslation } from "react-i18next";
import SelectableTestList from "../result_list/SelectableTestList";
import TitleWithInfoLink from "../new_test_ui/new_test_steps/common/TitleWithInfoLink";

export default function PatientPage(_props) {
  const { t } = useTranslation();
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uuid = params.get("uuid");
    getPatient(
      { uuid: uuid },
      (response) => {
        const pats = response.data;
        setPatient(pats[0]);
      },
      null,
    );
  }, []);

  return (
    <Container>
      <PatientInfoCard value={patient} withButton={false} />

      <Card my={"md"} withBorder shadow="lg" px={0} pt={0}>
        <Tabs keepMounted={false} variant="pills" defaultValue="tests" radius={0}>
          <Tabs.List grow style={{ gap: "0" }}>
            <Tabs.Tab value="tests" h={40}>
              {t("OpaReports.tests")}
            </Tabs.Tab>
            <Tabs.Tab value="summary">{t("OpaReports.summary")}</Tabs.Tab>
            <Tabs.Tab value="followup">{t("OpaReports.followup")}</Tabs.Tab>
            <Tabs.Tab value="overview">{t("OpaReports.overview")}</Tabs.Tab>
            <Tabs.Tab value="baseline">{t("OpaReports.baseline")}</Tabs.Tab>
          </Tabs.List>
          <Divider color="blue" mb={"md"} size={"sm"} />

          <Tabs.Panel value="tests" px={"md"}>
            <SelectableTestList
              patientId={patient ? patient.uuid : null}
              title={
                <TitleWithInfoLink
                  title={t("OpaReports.tests")}
                  tooltipText={t("OpaReports.testsTooltip")}
                  color={"dimmed"}
                  size={"h3"}
                />
              }
              selectable={false}
              filterable={true}
            />
          </Tabs.Panel>

          <Tabs.Panel value="summary" px={"md"}>
            <SelectableTestList
              patientId={patient ? patient.uuid : null}
              selectable={true}
              title={
                <TitleWithInfoLink
                  title={t("OpaReports.summary")}
                  tooltipText={t("OpaReports.summaryTooltip")}
                  color={"dimmed"}
                  size={"h3"}
                  description={t("OpaReports.baselineDescription")}
                />
              }
              filterable={true}
              alwaysShowFilter={true}
              defaultEye={"leftEye"}
              defaultTestType="24-2"
              testTypeFilter={"24-2,30-2"}
              generateButton={(selectedRows, baselineTests) =>
                window.open(
                  window.location.origin +
                    "/gpa-summary?uuid=" +
                    baselineTests.map((row) => row).join(",") +
                    "," +
                    selectedRows.map((row) => row).join(","),
                )
              }
              isGenerateDisabled={(selectedRows) => selectedRows.length < 3}
              showAfterBaseline={true}
              minimumTestNeeded={3}
              minimumTestNeededWarningText={t("OpaReports.minimumTestNeededWarningText")}
            />
          </Tabs.Panel>

          <Tabs.Panel value="followup" px={"md"}>
            <SelectableTestList
              patientId={patient ? patient.uuid : null}
              selectable={true}
              title={
                <TitleWithInfoLink
                  title={t("OpaReports.followup")}
                  tooltipText={t("OpaReports.followupTooltip")}
                  color={"dimmed"}
                  size={"h3"}
                  description={t("OpaReports.baselineDescription")}
                />
              }
              filterable={true}
              alwaysShowFilter={true}
              defaultEye={"leftEye"}
              defaultTestType="24-2"
              testTypeFilter={"24-2,30-2"}
              setDefaultSelectedTestsOnStart={(tests) => tests.slice(0, 3).map((test) => test.uuid)}
              generateButton={(selectedRows, baselineTests) =>
                window.open(
                  window.location.origin +
                    "/gpa-followup?uuid=" +
                    baselineTests.map((row) => row).join(",") +
                    "," +
                    selectedRows.map((row) => row).join(","),
                )
              }
              isGenerateDisabled={(selectedRows) => selectedRows.length < 3}
              showAfterBaseline={true}
              minimumTestNeeded={3}
              minimumTestNeededWarningText={t("OpaReports.minimumTestNeededWarningText")}
            />
          </Tabs.Panel>

          <Tabs.Panel value="baseline" px={"md"}>
            <SelectableTestList
              patientId={patient ? patient.uuid : null}
              selectable={true}
              title={
                <TitleWithInfoLink
                  title={t("OpaReports.baseline")}
                  tooltipText={t("OpaReports.baselineTooltip")}
                  color={"dimmed"}
                  size={"h3"}
                />
              }
              filterable={true}
              alwaysShowFilter={true}
              defaultEye={"leftEye"}
              defaultTestType="24-2"
              testTypeFilter={"24-2,30-2"}
              showSelectedOnTop={true}
              selectionLimit={2}
              ascending={true}
              baselinSet={true}
            />
          </Tabs.Panel>
          <Tabs.Panel value="overview" px={"md"}>
            <SelectableTestList
              patientId={patient ? patient.uuid : null}
              selectable={true}
              alwaysShowFilter={true}
              title={
                <TitleWithInfoLink
                  title={t("OpaReports.overview")}
                  tooltipText={t("OpaReports.overviewTooltip")}
                  color={"dimmed"}
                  size={"h3"}
                  description={t("OpaReports.baselineDescription")}
                />
              }
              filterable={true}
              defaultEye={"leftEye"}
              testTypeFilter={"24-2,30-2"}
              selectionLimit={9}
              generateButton={(selectedRows) =>
                window.open(
                  window.location.origin +
                    "/gpa-overview?uuid=" +
                    selectedRows.map((row) => row).join(","),
                )
              }
              isGenerateDisabled={(selectedRows) => selectedRows.length < 3}
              minimumTestNeeded={3}
              minimumTestNeededWarningText={t("OpaReports.minimumTestNeededWarningText")}
            />
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Container>
  );
}
